import {
  faBan,
  faPencil,
  faThumbsUp,
  faXmarkCircle,
  faTrash,
  faDownload,
  faStar,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Avatar,
  Card,
  Content,
  Data,
  Indicators,
  Layout,
  Modal,
  Navigation,
  Responsive,
  theme,
} from 'ui'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { EventNavigationDrawer } from './EventNavigationDrawer'
import { usePaginatedEvents } from './usePaginatedEvents'
import {
  useFindEvent,
  useCancelEvent,
  useDenyEvent,
  useDestroyEvent,
  usePublishEvent,
} from '../../../queries/useEventOperations'
import { useEventInvitations } from '../../../queries/useEventInvitations'
import { useEventInvitationEmailLogs } from '../../../queries/useEventInvitationEmailLogs'
import { useEventSuggestedMatches } from '../../../queries/useEventSuggestedMatches'
import { useEventRatings } from '../../../queries/useEventRatings'
import { Event, EventStatus } from '../../../queries/types'

interface ValidationError {
  message: string
  path?: string[]
}

const { Attribute, Section } = Data

const EMAIL_AUDIT_HEADER_CLASS = '!text-secondary min-w-0 truncate'
const EMAIL_AUDIT_CELL_CLASS = 'min-w-0 overflow-hidden'
const EMAIL_AUDIT_VALUE_CLASS = 'block w-full truncate leading-tight'

// Helper function to create tab structure for the Content.Tabs component
const createTabStructure = (
  statusCounts: any,
  matchesCount: number = 0,
  matchesLoading: boolean = false,
  eventStatus?: string,
  emailAuditCount: number = 0,
  emailAuditLoading: boolean = false
) => {
  // Base invitation tabs that are always shown
  const baseInvitationTabs = [
    { label: `Accepted (${statusCounts?.ACCEPTED || 0})`, value: 'ACCEPTED' },
    { label: `Declined (${statusCounts?.DECLINED || 0})`, value: 'DECLINED' },
    { label: `Pending (${statusCounts?.PENDING || 0})`, value: 'PENDING' },
  ]

  // Additional tabs for FINISHED events
  const finishedEventTabs = [
    {
      label: `Cancelled (${statusCounts?.CANCELLED || 0})`,
      value: 'CANCELLED',
    },
    { label: `Finished (${statusCounts?.FINISHED || 0})`, value: 'FINISHED' },
    {
      label: `Needs Review (${statusCounts?.NEEDS_REVIEW || 0})`,
      value: 'NEEDS_REVIEW',
    },
  ]

  // Determine which tabs to include
  const invitationTabs =
    eventStatus === EventStatus.Finished
      ? [...baseInvitationTabs, ...finishedEventTabs]
      : baseInvitationTabs

  // Calculate total count for "All" tab
  const totalCount = invitationTabs.reduce(
    (sum, tab) => sum + (statusCounts?.[tab.value] || 0),
    0
  )

  const allTab = {
    label: `All (${totalCount})`,
    value: 'all',
  }

  const matchesLabel = matchesLoading
    ? 'Matches (...)'
    : `Matches (${matchesCount})`
  const matchesTab = { label: matchesLabel, value: 'matches' }
  const emailAuditLabel = emailAuditLoading
    ? 'Email Audit (...)'
    : `Email Audit (${emailAuditCount})`
  const emailAuditTab = { label: emailAuditLabel, value: 'email_audit' }

  return {
    invitationTabs: [allTab, ...invitationTabs],
    matchesTab,
    emailAuditTab,
    allTabs: [allTab, ...invitationTabs, matchesTab, emailAuditTab],
  }
}

// Transform API invitation data to match existing InvitationCard component interface
const transformInvitationData = (invitation: any) => {
  const profile = invitation.invitee
  return {
    profileColor: profile.color || '#ccc',
    profileName: `${profile.first_name || ''} ${
      profile.last_name || ''
    }`.trim(),
    profileIconUrl: profile.avatar_url || '',
    profileDescription: [
      profile.role,
      profile.company_name,
      profile.school_name,
    ]
      .filter(Boolean)
      .join(' • '),
    profileDescription2: `Invited ${DateTime.fromISO(
      invitation.invited_at
    ).toFormat('MMM dd, yyyy')}${
      invitation.responded_at
        ? ` • Responded ${DateTime.fromISO(invitation.responded_at).toFormat(
            'MMM dd, yyyy'
          )}`
        : ''
    }`,
    status: invitation.status,
    createdAt: invitation.invited_at,
    profileId: profile.profile_id,
  }
}

// Transform match data for ExtendedProfile component
const transformMatchData = (match: any) => {
  return {
    id: match.id,
    name: match.name,
    title: match.title,
    color: match.color,
    avatarUrl: match.avatarUrl,
    keywords: match.keywords,
    hasUnreadMessages: false,
  }
}

// Transform API rating data to match existing UI component interface
const transformRatingData = (rating: any) => {
  return {
    id: rating.id,
    profileName: rating.profile_name || 'Unknown User',
    profileColor: rating.profile_color || '#ccc',
    profileAvatarUrl: rating.profile_avatar_url || '',
    score: rating.score || 0,
    comments: rating.comments || '',
  }
}

const isDryRunEmailSource = (source?: string) =>
  source === 'OPEN_INVITE_SYSTEM_DRY_RUN' ||
  source === 'EVENT_PUBLISH_INVITE_DRY_RUN'

const getEmailAuditStatusLabel = (status?: string, source?: string) => {
  if (status === 'SENT' && isDryRunEmailSource(source)) {
    return 'SENT (Dry Run)'
  }
  return status || 'n/a'
}

const getEmailAuditSourceLabel = (source?: string) => {
  switch (source) {
    case 'OPEN_INVITE_SYSTEM':
      return 'Open Invite'
    case 'OPEN_INVITE_SYSTEM_DRY_RUN':
      return 'Open Invite (Dry Run)'
    case 'EVENT_PUBLISH_INVITE_DRY_RUN':
      return 'Publish Invite (Dry Run)'
    case 'EVENT_PUBLISH_INVITE':
      return 'Publish Invite'
    default:
      return source || 'n/a'
  }
}

const renderEmailAuditText = (
  value?: string | null,
  fallback: string = 'n/a'
) => {
  const resolvedValue = value && value.trim().length > 0 ? value : fallback
  return (
    <span className={EMAIL_AUDIT_VALUE_CLASS} title={resolvedValue}>
      {resolvedValue}
    </span>
  )
}

const formatEmailAuditTimestamp = (value?: string | null) => {
  if (!value) {
    return 'n/a'
  }

  return DateTime.fromISO(value).toFormat('MMM dd, yyyy h:mm a')
}

export const EventDetailView: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  // Drawer appearance
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('compact')

  const { locationSearch } = usePaginatedEvents({ fetchPolicy: 'cache-first' })

  const [updatingEvent, setUpdatingEvent] = useState(false)
  const [currentInvitationPage, setCurrentInvitationPage] = useState(0)
  const [invitationPageSize] = useState(20)
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined
  )
  const [selectedTab, setSelectedTab] = useState<
    'invitations' | 'matches' | 'email_audit'
  >('invitations')
  const [currentMatchesPage, setCurrentMatchesPage] = useState(0)
  const [matchesPageSize] = useState(20)
  const [currentEmailAuditPage, setCurrentEmailAuditPage] = useState(0)
  const [emailAuditPageSize] = useState(20)

  const { isLoading: loadingEvent, data, error: loadError } = useFindEvent(
    params.id ?? ''
  )
  const {
    data: invitationsData,
    isLoading: loadingInvitations,
  } = useEventInvitations(params.id ?? '', {
    limit: invitationPageSize,
    skip: currentInvitationPage * invitationPageSize,
    status: selectedStatus,
  })

  const {
    data: matchesData,
    isLoading: loadingMatches,
    error: matchesError,
  } = useEventSuggestedMatches(
    params.id ?? '',
    currentMatchesPage + 1, // API uses 1-based pagination
    matchesPageSize
  )

  const {
    data: emailAuditData,
    isLoading: loadingEmailAudit,
  } = useEventInvitationEmailLogs(
    params.id ?? '',
    {
      limit: emailAuditPageSize,
      skip: currentEmailAuditPage * emailAuditPageSize,
    },
    {
      enabled: !!params.id,
    }
  )

  // Check if the error is specifically about missing keywords
  const hasKeywordsError =
    matchesError?.message?.includes('keywords') ||
    matchesError?.message?.includes('No keywords found') ||
    matchesError?.message?.includes('embeddings')

  const {
    data: ratingsData,
    isLoading: loadingRatings,
    error: ratingsError,
  } = useEventRatings({
    eventId: params.id ?? '',
    limit: 100, // Fetch up to 100 ratings to avoid pagination UI initially
    offset: 0,
  })

  const [cancelEvent, { error: cancelError }] = useCancelEvent()
  const [denyEvent, { error: denyError }] = useDenyEvent()
  const [destroyEvent, { error: destroyError }] = useDestroyEvent()
  const [publishEvent, { error: publishError }] = usePublishEvent()
  const networkError =
    cancelError ||
    denyError ||
    destroyError ||
    loadError ||
    publishError ||
    (matchesError && !hasKeywordsError) || // Only show matches error if it's not a keywords issue
    ratingsError
  const [errorMessage, setErrorMessage] = useState(
    undefined as string | undefined
  )

  useEffect(() => {
    if (networkError && errorMessage !== networkError?.message) {
      setErrorMessage(networkError?.message)
    }
  }, [networkError, errorMessage])

  const handleDismissError = () => {
    setErrorMessage(undefined)
    navigate(`/admin/events/`)
  }

  const event = data?.response?.event ?? ({} as Event)
  const id = params.id
  const hasAttachments = event.attachments?.length ?? -1 > 0
  const eventHasNoKeywords =
    !event?.keywords ||
    (Array.isArray(event.keywords) && event.keywords.length === 0)

  // Compute ratings array from API response
  console.log(ratingsData)
  const ratings = ratingsData?.ratings?.map(transformRatingData) || []
  const hasRatings = ratings.length > 0

  const [confirmStatus, setConfirmStatus] = useState(null as EventStatus | null)

  const handleEditClick = () => {
    navigate(`/admin/events/edit/${id}${locationSearch}`)
  }
  const handleStatusUpdate = (status: EventStatus) => () => {
    setConfirmStatus(status)
  }
  const dismissModal = () => {
    setConfirmStatus(null)
  }

  const messageForConfirmStatus = () => {
    switch (confirmStatus) {
      case EventStatus.Cancelled:
        return 'This event will be cancelled and all invited attendees will be notified.'
      case EventStatus.Denied:
        return 'The organizer will have to create a new alternate event and this event will be closed.'
      case EventStatus.Deleted:
        return 'Deleting this event will permenantly delete the event and associated invitations from the system. You cannot undo this action.'
      case EventStatus.Published:
        return 'This event will become public and users can be invited.'
      default:
        return 'This will alter the state of this event and my prevent other users from accessing it.'
    }
  }

  const optionalMessageLabel = () => {
    switch (confirmStatus) {
      case EventStatus.Cancelled:
        return 'Let attendees know why this event has been cancelled:'
      case EventStatus.Denied:
        return 'Let organizer know why this event has been denied'
      default:
        return null
    }
  }

  const handleTabSelection = (tab: any) => {
    if (tab.value === 'matches') {
      setSelectedTab('matches')
      setCurrentMatchesPage(0)
    } else if (tab.value === 'email_audit') {
      setSelectedTab('email_audit')
      setCurrentEmailAuditPage(0)
    } else {
      setSelectedTab('invitations')
      const status = tab.value === 'all' ? undefined : tab.value
      setSelectedStatus(status)
      setCurrentInvitationPage(0)
    }
  }

  // Helper function for current tab value
  const getCurrentTabValue = () => {
    if (selectedTab === 'matches') return 'matches'
    if (selectedTab === 'email_audit') return 'email_audit'
    return selectedStatus || 'all'
  }

  const renderInvitedProfilesSection = () => (
    <Section>
      <Attribute label="Invited Profiles:">
        <div className="flex flex-col w-full">
          {loadingInvitations ? (
            <Indicators.Spinner />
          ) : !invitationsData ? (
            <div className="text-gray-500">No invitation data available</div>
          ) : (
            <>
              {/* Status Filter Tabs */}
              {invitationsData.status_counts && (
                <div className="mb-6 w-full">
                  <Content.Tabs
                    className="w-full"
                    tabs={
                      createTabStructure(
                        invitationsData.status_counts,
                        matchesData?.pagination?.totalItems || 0,
                        loadingMatches,
                        event.status,
                        emailAuditData?.pagination?.total_count || 0,
                        loadingEmailAudit
                      ).allTabs
                    }
                    currentValue={getCurrentTabValue()}
                    onSelectTab={handleTabSelection}
                  />
                </div>
              )}

              {/* Content based on selected tab */}
              {selectedTab === 'matches' ? (
                <div className="flex flex-col">
                  {loadingMatches ? (
                    <Indicators.Spinner />
                  ) : hasKeywordsError || eventHasNoKeywords ? (
                    <div className="text-center py-8">
                      <div className="text-gray-500 mb-4">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-4xl mb-4 text-gray-300"
                        />
                        <h3 className="text-lg font-semibold mb-2">
                          No Keywords for Matching
                        </h3>
                        <p className="mb-4">
                          This event doesn't have keywords defined, so suggested
                          matches cannot be generated.
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Add keywords to this event to enable powerful
                          AI-driven profile matching.
                        </p>
                      </div>
                      <Content.ActionButton
                        label="Edit Event & Add Keywords"
                        icon={faPencil}
                        onClick={handleEditClick}
                        className="inline-flex"
                      />
                    </div>
                  ) : !matchesData ? (
                    <div className="text-gray-500 text-center py-8">
                      No matches data available
                    </div>
                  ) : (
                    <>
                      {!matchesData.data || matchesData.data.length === 0 ? (
                        <div className="text-gray-500 text-center py-8">
                          No matches found for this event
                        </div>
                      ) : (
                        <>
                          {/* Matches Grid - Single Column Layout */}
                          <div className="grid grid-cols-1 gap-4">
                            {matchesData.data.map((match) => (
                              <Card.ExtendedProfile
                                key={match.id}
                                profile={transformMatchData(match)}
                                matchDescription={
                                  match.publicBio ??
                                  match.about ??
                                  'Strong match based on profile criteria'
                                }
                                matchPercent={match.matchScore?.relevance ?? 0}
                                favorite={match.favorite}
                                className="px-2 py-3 mt-2 border border-body/5"
                                onSelectProfile={(profileId) =>
                                  navigate(`/admin/profiles/show/${profileId}`)
                                }
                              />
                            ))}
                          </div>

                          {/* Pagination Info */}
                          <div className="text-sm text-gray-500 text-center mt-4 mb-2">
                            Showing {currentMatchesPage * matchesPageSize + 1}{' '}
                            to{' '}
                            {Math.min(
                              (currentMatchesPage + 1) * matchesPageSize,
                              matchesData.pagination.totalItems
                            )}{' '}
                            of {matchesData.pagination.totalItems} matches
                          </div>

                          {/* Pagination Controls */}
                          {matchesData.pagination.totalPages > 1 && (
                            <Navigation.SimplePaginationBar
                              currentPage={matchesData.pagination.currentPage}
                              pageCount={matchesData.pagination.totalPages}
                              onPageClick={(page) =>
                                setCurrentMatchesPage(page - 1)
                              }
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : selectedTab === 'email_audit' ? (
                <div className="flex flex-col">
                  {loadingEmailAudit ? (
                    <Indicators.Spinner />
                  ) : !emailAuditData ? (
                    <div className="text-gray-500 text-center py-8">
                      No email audit data available
                    </div>
                  ) : !emailAuditData.logs ||
                    emailAuditData.logs.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">
                      No invitation email logs found for this event
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto w-full">
                        <Data.Table>
                          <Data.Row>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="13%"
                            >
                              Status
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="14%"
                            >
                              Invitee
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="16%"
                            >
                              Email
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="11%"
                            >
                              Sent At
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="11%"
                            >
                              Attempted At
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="11%"
                            >
                              Source
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="14%"
                            >
                              Message ID
                            </Data.Header>
                            <Data.Header
                              className={EMAIL_AUDIT_HEADER_CLASS}
                              width="10%"
                            >
                              Error
                            </Data.Header>
                          </Data.Row>
                          {emailAuditData.logs.map((log) => (
                            <Data.Row key={log.id}>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="13%"
                              >
                                {renderEmailAuditText(
                                  getEmailAuditStatusLabel(log.status, log.source)
                                )}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="14%"
                              >
                                {log.profile?.profile_id ? (
                                  <Link
                                    className={EMAIL_AUDIT_VALUE_CLASS}
                                    title={`${log.profile.first_name || ''} ${
                                      log.profile.last_name || ''
                                    }`.trim()}
                                    to={`/admin/profiles/show/${log.profile.profile_id}`}
                                  >
                                    {`${log.profile.first_name || ''} ${
                                      log.profile.last_name || ''
                                    }`.trim()}
                                  </Link>
                                ) : (
                                  renderEmailAuditText('Unknown')
                                )}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="16%"
                              >
                                {renderEmailAuditText(log.email_address)}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="11%"
                              >
                                {renderEmailAuditText(
                                  formatEmailAuditTimestamp(log.sent_at)
                                )}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="11%"
                              >
                                {renderEmailAuditText(
                                  formatEmailAuditTimestamp(log.attempted_at)
                                )}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="11%"
                              >
                                {renderEmailAuditText(
                                  getEmailAuditSourceLabel(log.source)
                                )}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="14%"
                              >
                                {renderEmailAuditText(log.message_id)}
                              </Data.Cell>
                              <Data.Cell
                                className={EMAIL_AUDIT_CELL_CLASS}
                                width="10%"
                              >
                                {renderEmailAuditText(log.error_message)}
                              </Data.Cell>
                            </Data.Row>
                          ))}
                        </Data.Table>
                      </div>

                      <div className="text-sm text-gray-500 text-center mt-4 mb-2">
                        Showing {emailAuditData.pagination.skip + 1} to{' '}
                        {Math.min(
                          emailAuditData.pagination.skip +
                            emailAuditData.logs.length,
                          emailAuditData.pagination.total_count
                        )}{' '}
                        of {emailAuditData.pagination.total_count} email logs
                      </div>

                      {emailAuditData.pagination.total_pages > 1 && (
                        <Navigation.SimplePaginationBar
                          currentPage={emailAuditData.pagination.current_page}
                          pageCount={emailAuditData.pagination.total_pages}
                          onPageClick={(page) =>
                            setCurrentEmailAuditPage(page - 1)
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {!invitationsData.invitations ||
                  invitationsData.invitations.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">
                      {selectedStatus
                        ? `No ${selectedStatus.toLowerCase()} invitations found for this event`
                        : 'No invitations found for this event'}
                    </div>
                  ) : (
                    <>
                      {/* Invitation Cards - 3 Column Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {invitationsData.invitations.map((invitation) => {
                          const transformedInvite = transformInvitationData(
                            invitation
                          )
                          return (
                            <Card.InvitationAttendee
                              key={`${invitation.profile_id}-${invitation.invited_at}`}
                              invite={transformedInvite}
                              showStatus={true}
                              onClick={() =>
                                navigate(
                                  `/admin/profiles/show/${transformedInvite.profileId}`
                                )
                              }
                            />
                          )
                        })}
                      </div>

                      {/* Pagination Info */}
                      <div className="text-sm text-gray-500 text-center mt-4 mb-2">
                        Showing {invitationsData.pagination.skip + 1} to{' '}
                        {Math.min(
                          invitationsData.pagination.skip +
                            invitationsData.invitations.length,
                          invitationsData.pagination.total_count
                        )}{' '}
                        of {invitationsData.pagination.total_count} invitations
                      </div>

                      {/* Pagination Controls */}
                      {invitationsData.pagination.total_pages > 1 && (
                        <Navigation.SimplePaginationBar
                          currentPage={invitationsData.pagination.current_page}
                          pageCount={invitationsData.pagination.total_pages}
                          onPageClick={(page) =>
                            setCurrentInvitationPage(page - 1)
                          }
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Attribute>
    </Section>
  )

  const handleConfirmStatus = async (reason?: string | null) => {
    setUpdatingEvent(true)
    let errors = [] as ValidationError[] | null | undefined
    switch (confirmStatus) {
      case EventStatus.Cancelled:
        const { data: cancelData } = await cancelEvent({
          variables: { eventId: event.id, reason },
        })
        errors = cancelData?.response.errors as ValidationError[]
        break
      case EventStatus.Denied:
        const { data: denyData } = await denyEvent({
          variables: { eventId: event.id, reason },
        })
        errors = denyData?.response.errors as ValidationError[]
        break
      case EventStatus.Deleted:
        const { data: deleteData } = await destroyEvent({
          variables: { eventId: event.id },
        })
        errors = deleteData?.response.errors as ValidationError[]
        if (!errors || errors?.length < 1) {
          navigate(`/admin/events${locationSearch}`)
          return
        }
        break
      case EventStatus.Published:
        const { data: published } = await publishEvent({
          variables: { eventId: event.id },
        })
        errors = published?.response.errors as ValidationError[]
        break
      default:
        break
    }
    setConfirmStatus(null)
    setUpdatingEvent(false)
    if (errors && errors.length > 0) {
      setErrorMessage(errors[0]?.message)
    }
  }

  return (
    <div
      className="flex-col flex sm:flex-row flex flex-grow w-full"
      style={{
        opacity:
          loadingEvent ||
          (selectedTab === 'matches' && loadingMatches) ||
          (selectedTab === 'email_audit' && loadingEmailAudit) ||
          loadingRatings
            ? 0.5
            : 1,
      }}
    >
      <Helmet title={`${event ? event.name : 'Event'} - MCSS Pro Connect`} />
      <EventNavigationDrawer />
      <Layout.PaddedContent>
        <div className="flex flex-col pb-24 w-full">
          <div className="flex flex-col sm:flex-row w-full">
            <article className="flex-col flex mr-0 sm:mr-4 w-full">
              <Content.Body>
                <Navigation.ReturnLink to={`/admin/events${locationSearch}`}>
                  Return to Events
                </Navigation.ReturnLink>
              </Content.Body>
              <Content.Heading>
                {loadingEvent ? (
                  <div className="flex">
                    <div className="mr-auto">
                      <Indicators.Spinner />
                    </div>
                  </div>
                ) : event ? (
                  event.name
                ) : (
                  'n/a'
                )}
              </Content.Heading>

              <Section>
                <Attribute
                  label="Status:"
                  value={loadingEvent ? '-------------' : event.status}
                />
                <Attribute
                  label="Created On:"
                  value={DateTime.fromISO(event.created_at || '').toFormat(
                    'MMM, dd, yyyy'
                  )}
                />
              </Section>

              <Section>
                <Attribute label="Occurs At:">
                  {loadingEvent ? (
                    '-------------'
                  ) : !event.dates?.times || event.dates.times.length === 0 ? (
                    <Content.Body marginClassNames="m-0">TBD</Content.Body>
                  ) : (
                    <div className="flex flex-col">
                      {/* Render each date/time entry by combining dates and times */}
                      {event.dates.dates?.map((dateEntry, dateIndex) => {
                        // If there are corresponding times, use them; otherwise just show the date range
                        const timeEntry = event.dates.times?.[dateIndex]

                        if (timeEntry && timeEntry.start && timeEntry.end) {
                          // Combine date from dates array with time from times array
                          const startDateStr = `${
                            dateEntry.startDate?.split('T')[0]
                          }T${timeEntry.start}:00`
                          const endDateStr = `${
                            (dateEntry.endDate ?? dateEntry.startDate)?.split(
                              'T'
                            )[0]
                          }T${timeEntry.end}:00`

                          return (
                            <Content.Body
                              marginClassNames="m-0"
                              key={dateEntry.startDate || dateIndex}
                            >
                              {DateTime.fromISO(startDateStr).toFormat(
                                'MMM, dd, yyyy hh:mm a'
                              )}{' '}
                              <br />
                              until{' '}
                              {DateTime.fromISO(endDateStr).toFormat(
                                'MMM, dd, yyyy hh:mm a'
                              )}
                            </Content.Body>
                          )
                        } else {
                          // No times, just show the date range
                          return (
                            <Content.Body
                              marginClassNames="m-0"
                              key={dateEntry.startDate || dateIndex}
                            >
                              {DateTime.fromISO(dateEntry.startDate).toFormat(
                                'MMM, dd, yyyy'
                              )}{' '}
                              {dateEntry.endDate &&
                                dateEntry.endDate !== dateEntry.startDate && (
                                  <>
                                    -{' '}
                                    {DateTime.fromISO(
                                      dateEntry.endDate
                                    ).toFormat('MMM, dd, yyyy')}
                                  </>
                                )}
                            </Content.Body>
                          )
                        }
                      })}

                      {/* Flexible dates indicator */}
                      {event.flexible_dates && (
                        <Content.Bubble className="mt-2 w-fit">
                          Dates are Flexible
                        </Content.Bubble>
                      )}
                    </div>
                  )}
                </Attribute>

                <Attribute label="Created By:">
                  {loadingEvent ? (
                    '-------------'
                  ) : event.organizer ? (
                    <Link to={`/admin/profiles/show/${event.organizer.id}`}>
                      {event.organizer.firstName} {event.organizer.lastName}
                    </Link>
                  ) : (
                    ''
                  )}
                </Attribute>
              </Section>

              {event.connectionPreference ? (
                <Section>
                  <Attribute
                    label="Connection Preference:"
                    value={event.connectionPreference as string[]}
                  />
                  <Attribute
                    label="Grade Levels:"
                    value={event.gradeLevels as string[]}
                  />
                </Section>
              ) : null}

              <Section>
                {event.description ? (
                  <Attribute label="Description:" value={event.description} />
                ) : null}
              </Section>

              <Section>
                {event.expectation_statement ? (
                  <Attribute
                    label="Expectation Statement:"
                    value={event.expectation_statement}
                    noTruncate={true}
                  />
                ) : null}
              </Section>

              <Section>
                {event.accomplish_statement ? (
                  <Attribute
                    label="Accomplishment Statement:"
                    value={event.accomplish_statement}
                    noTruncate={true}
                  />
                ) : null}
              </Section>

              {event.virtual_meeting_url ? (
                <Section>
                  <Attribute
                    label="Virtual Meeting URL:"
                    value={event.virtual_meeting_url}
                  />
                </Section>
              ) : null}

              {hasAttachments ? (
                <Section>
                  <Attribute label="Additional Documents / Information:">
                    <div className="flex-col flex">
                      {[...(event.attachments ?? [])]
                        .sort((a, b) =>
                          (a?.position ?? 1) > (b?.position ?? 0) ? 1 : -1
                        )
                        .map((a) => (
                          <div
                            key={a?.id ?? ''}
                            className="flex items-center min-w-0"
                          >
                            <div className="my-0 mr-2 shrink-0">
                              <Content.Text>
                                <FontAwesomeIcon icon={faDownload} />
                              </Content.Text>
                            </div>
                            <Content.Text className="min-w-0 flex-1">
                              <a
                                className="block truncate"
                                href={a?.downloadUrl ?? '#'}
                              >
                                {a?.name}
                              </a>
                            </Content.Text>
                          </div>
                        ))}
                    </div>
                  </Attribute>
                </Section>
              ) : null}

              <Section>
                <Attribute label="Ratings:">
                  {loadingRatings ? (
                    <div className="flex justify-center py-4">
                      <Indicators.Spinner />
                    </div>
                  ) : ratingsError ? (
                    <div className="text-red-500 py-4">
                      Error loading ratings: {ratingsError.message}
                    </div>
                  ) : hasRatings ? (
                    <div className="flex flex-wrap my-auto">
                      {ratings.map((r) => (
                        <Card.View key={r.id} className="py-2 px-3">
                          <Avatar.Simple
                            name={r.profileName}
                            maxInitials={1}
                            color={r.profileColor}
                            imageUrl={r.profileAvatarUrl}
                            size={32}
                          />
                          <div className="flex-col flex ml-2">
                            <div className="font-semibold text-sm">
                              {r.profileName}
                            </div>
                            <div className="my-2">{r.comments}</div>
                            <div className="flex">
                              {new Array(5).fill(null).map((_, i) => (
                                <div
                                  key={`star-${i}`}
                                  style={{ color: theme.colorAccent }}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      r.score >= i + 1 ? faStar : faStarOutline
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card.View>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 py-4">No ratings yet</div>
                  )}
                </Attribute>
              </Section>

              {event.contentAreas ? (
                <Section>
                  <Attribute
                    label="Content Areas:"
                    value={event.contentAreas as string[]}
                  />
                </Section>
              ) : null}
            </article>
            <div className="flex-col flex mb-auto w-full sm:w-1/2 md:w-1/3 mt-2 md:mt-4">
              <div className="mb-2 mx-auto">
                {loadingEvent ? (
                  <Indicators.Spinner size="4x" />
                ) : (
                  <Avatar.Simple
                    name={event.name}
                    maxInitials={1}
                    color={event.color || undefined}
                    imageUrl={event.iconUrl || ''}
                    size={128}
                  />
                )}
              </div>
              <Content.ActionButton
                className="mt-0"
                label="Edit"
                icon={faPencil}
                onClick={handleEditClick}
                loading={updatingEvent}
              />
              {[EventStatus.Published].includes(event.status as EventStatus) ? (
                <Content.ActionButton
                  label="Cancel"
                  icon={faXmarkCircle}
                  appearance="destructive"
                  onClick={handleStatusUpdate(EventStatus.Cancelled)}
                  loading={updatingEvent}
                />
              ) : null}
              {[EventStatus.Cancelled, EventStatus.Denied].includes(
                event.status as EventStatus
              ) ? (
                <Content.ActionButton
                  label="Delete"
                  icon={faTrash}
                  appearance="destructive"
                  onClick={handleStatusUpdate(EventStatus.Deleted)}
                  loading={updatingEvent}
                />
              ) : null}
              {[EventStatus.Draft, 'NEEDS_APPROVAL'].includes(
                event.status as EventStatus
              ) ? (
                <React.Fragment>
                  <Content.ActionButton
                    className="mt-0"
                    label="Approve"
                    icon={faThumbsUp}
                    onClick={handleStatusUpdate(EventStatus.Published)}
                    loading={updatingEvent}
                  />
                  <Content.ActionButton
                    className="mt-0"
                    label="Deny"
                    icon={faBan}
                    appearance="destructive"
                    onClick={handleStatusUpdate(EventStatus.Denied)}
                    loading={updatingEvent}
                  />
                </React.Fragment>
              ) : (
                <span />
              )}
            </div>
          </div>
          <div className="w-full mt-4">{renderInvitedProfilesSection()}</div>
        </div>
      </Layout.PaddedContent>
      {confirmStatus ? (
        <Modal.Dialog
          title="Are You Sure?"
          message={messageForConfirmStatus()}
          onDismiss={dismissModal}
          onConfirm={handleConfirmStatus}
          allowMessage={optionalMessageLabel() ? true : false}
          loading={updatingEvent}
          messageLabel={optionalMessageLabel() ?? ''}
          destructive={confirmStatus !== EventStatus.Published}
        />
      ) : null}
      {errorMessage ? (
        <Modal.Dialog
          title="Event Could Not Update"
          message={errorMessage}
          onDismiss={handleDismissError}
          cancelLabel={'OK'}
          allowMessage={optionalMessageLabel() ? true : false}
          loading={updatingEvent}
          messageLabel={optionalMessageLabel() ?? ''}
        />
      ) : null}
    </div>
  )
}
